import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '../provider';
import { AnalyticsEvent, UserIdentity } from '../types';
import { isLocalOrTestEnvironment } from '../utils';

/**
 * Hook to automatically track page views on route changes
 */
export const usePageTracking = (enabled: boolean = true): void => {
  const location = useLocation();
  const { trackPageView } = useAnalytics();
  const previousLocation = useRef<string>('');
  const isLocalOrTest = isLocalOrTestEnvironment();

  useEffect(() => {
    if (!enabled || isLocalOrTest) return;

    const currentPath = location.pathname + location.search;

    // Only track if the location actually changed
    if (currentPath !== previousLocation.current) {
      trackPageView({
        page: currentPath,
        path: currentPath,
        title: document.title
      });
      previousLocation.current = currentPath;
    }
  }, [location, enabled, trackPageView]);
};

/**
 * Hook to track custom events with debouncing
 */
export const useEventTracking = (debounceMs: number = 300) => {
  const { track } = useAnalytics();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const isLocalOrTest = isLocalOrTestEnvironment();

  if (isLocalOrTest) return;

  const trackEvent = (event: AnalyticsEvent): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      track(event);
    }, debounceMs);
  };

  const trackImmediate = (event: AnalyticsEvent): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    track(event);
  };

  return { trackEvent, trackImmediate };
};

/**
 * Hook to identify users and track user sessions
 */
export const useUserTracking = (user?: UserIdentity): void => {
  const { identify } = useAnalytics();
  const identifiedUser = useRef<string>('');
  const isLocalOrTest = isLocalOrTestEnvironment();

  useEffect(() => {
    if (!user || !user.username || isLocalOrTest) return;

    // Only identify if this is a different user
    if (user.username !== identifiedUser.current) {
      identify(user);
      identifiedUser.current = user.username;
    }
  }, [user, identify]);
};

/**
 * Hook for error tracking
 */
export const useErrorTracking = () => {
  const { captureException, captureMessage } = useAnalytics();

  const trackError = (error: Error, context?: Record<string, any>): void => {
    captureException(error, context);
  };

  const trackWarning = (
    message: string,
    context?: Record<string, any>
  ): void => {
    captureMessage(message, 'warning');
    if (context) {
      captureException(new Error(message), context);
    }
  };

  const trackInfo = (message: string): void => {
    captureMessage(message, 'info');
  };

  return {
    trackError,
    trackWarning,
    trackInfo
  };
};

export { useAnalytics } from '../provider';
